import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, FileText, Sparkles, Copy, Check, Image as ImageIcon, Wand2, Share2, Send, Settings } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { Link } from "react-router-dom";

const TONES = ["professional", "casual", "expert", "friendly", "persuasive"];
const LANGUAGES = ["English", "Spanish", "French", "German", "Italian", "Portuguese", "Dutch", "Polish", "Japanese", "Korean", "Chinese", "Arabic", "Hindi", "Turkish", "Swedish", "Finnish", "Greek", "Czech", "Hungarian", "Romanian"];

function mdToHtml(md = "") {
  // tiny safe-ish markdown: headings, bold, italic, lists, paragraphs
  let html = md
    .replace(/^### (.*)$/gm, "<h3>$1</h3>")
    .replace(/^## (.*)$/gm, "<h2>$1</h2>")
    .replace(/^# (.*)$/gm, "<h2>$1</h2>")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/^- (.*)$/gm, "<li>$1</li>");
  html = html.replace(/(?:<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`);
  html = html.split(/\n{2,}/).map((p) => (p.startsWith("<") ? p : `<p>${p}</p>`)).join("\n");
  return html;
}

export default function BlogPage() {
  const location = useLocation();
  const { active } = useProjects();
  const [topic, setTopic] = useState(location?.state?.topic || "");
  const [keyword, setKeyword] = useState(location?.state?.keyword || "");
  const [tone, setTone] = useState("professional");
  const [language, setLanguage] = useState("English");
  const [wordCount, setWordCount] = useState(1200);
  const [genImage, setGenImage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [titlesLoading, setTitlesLoading] = useState(false);
  const [titles, setTitles] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [imgRegen, setImgRegen] = useState(false);

  const fetchHistory = () => api.get("/blog/posts").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { fetchHistory(); }, []);

  const genTitles = async () => {
    if (!topic || !keyword) {
      setError("Topic and keyword are required");
      return;
    }
    setTitlesLoading(true);
    setError("");
    try {
      const { data } = await api.post("/blog/titles", { topic, keyword, tone, language, word_count: wordCount });
      setTitles(data?.titles || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setTitlesLoading(false); }
  };

  const generate = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/blog/generate", {
        topic, keyword, tone, word_count: wordCount, language,
        project_id: active?.id || null, generate_image: genImage,
      });
      setResult(data);
      fetchHistory();
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  const regenImage = async () => {
    if (!result) return;
    setImgRegen(true);
    try {
      const { data } = await api.post("/blog/image", {
        prompt: result.result.featured_image_prompt,
        blog_id: result.id,
      });
      setResult((r) => ({ ...r, result: { ...r.result, featured_image: data.featured_image || data.image } }));
    } catch {}
    finally { setImgRegen(false); }
  };

  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const broadcast = async () => {
    if (!result) return;
    setBroadcasting(true); setBroadcastResult(null);
    try {
      const { data } = await api.post("/social/broadcast", { blog_id: result.id });
      setBroadcastResult({ ok: true, msg: `Broadcast to ${data.ok_count}/${data.total} channels` });
    } catch (e) {
      setBroadcastResult({ ok: false, msg: formatApiError(e) });
    }
    finally { setBroadcasting(false); }
  };

  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const publish = async () => {
    if (!result) return;
    setPublishing(true); setPublishResult(null);
    try {
      const { data } = await api.post("/integrations/publish", { blog_id: result.id });
      setPublishResult({ ok: data.ok_count > 0, msg: `Published to ${data.ok_count}/${data.total} sites`, results: data.results });
    } catch (e) {
      setPublishResult({ ok: false, msg: formatApiError(e) });
    }
    finally { setPublishing(false); }
  };

  const copyMd = () => {
    if (!result?.result?.content_markdown) return;
    navigator.clipboard.writeText(result.result.content_markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <DashboardLayout
      eyebrow="AI Blog Generator"
      title="Long-form posts. SEO-optimized. Ready to ship."
      subtitle="Generate full articles with FAQ, internal linking, meta tags and a featured image."
    >
      <div className="grid lg:grid-cols-[400px_1fr] gap-6">
        {/* Form */}
        <form onSubmit={generate} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 space-y-4 h-fit lg:sticky lg:top-24" data-testid="blog-form">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Topic</Label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} required placeholder="e.g. how to rank for AI tools"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="blog-topic" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Target Keyword</Label>
            <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} required placeholder="e.g. ai seo tools"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="blog-keyword" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="blog-tone-trigger"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                  {TONES.map((t) => <SelectItem key={t} value={t} data-testid={`blog-tone-${t}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Word count</Label>
              <Select value={String(wordCount)} onValueChange={(v) => setWordCount(parseInt(v))}>
                <SelectTrigger className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="blog-words-trigger"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                  <SelectItem value="600">600</SelectItem>
                  <SelectItem value="1200">1200</SelectItem>
                  <SelectItem value="2000">2000</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button type="button" onClick={genTitles} disabled={titlesLoading} variant="outline"
                  className="w-full h-11 bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="blog-titles-btn">
            {titlesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Brainstorm titles</>}
          </Button>
          {titles.length > 0 && (
            <ul className="space-y-1.5 max-h-44 overflow-y-auto border border-zinc-800 rounded-sm p-2" data-testid="blog-titles-list">
              {titles.map((t, i) => (
                <li key={i} onClick={() => setTopic(t)}
                    className="text-sm text-zinc-300 px-2 py-1.5 hover:bg-zinc-900 rounded-sm cursor-pointer">
                  {t}
                </li>
              ))}
            </ul>
          )}
          <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="blog-generate-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-2" /> Generate full post</>}
          </Button>
          {error && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="blog-error">{error}</div>}
        </form>

        {/* Result */}
        <div className="min-w-0">
          {loading && <div className="bg-zinc-950 border border-zinc-800 rounded-sm h-96 animate-pulse" />}
          {result && <BlogResult data={result.result} blogId={result.id} onCopy={copyMd} copied={copied} onRegenImage={regenImage} imgRegen={imgRegen} onBroadcast={broadcast} broadcasting={broadcasting} broadcastResult={broadcastResult} onPublish={publish} publishing={publishing} publishResult={publishResult} autoPublishResults={result.auto_publish_results} />}
          {!result && !loading && history.length > 0 && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="blog-history">
              <div className="px-6 py-4 border-b border-zinc-800">
                <span className="label-eyebrow">Saved drafts</span>
              </div>
              <ul className="divide-y divide-zinc-900">
                {history.map((h) => (
                  <li key={h.id} className="px-6 py-4 hover:bg-zinc-900/50 cursor-pointer transition-colors"
                      onClick={() => setResult(h)} data-testid={`blog-draft-${h.id}`}>
                    <div className="text-sm text-zinc-200 truncate">{h.result?.title || h.topic}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {new Date(h.created_at).toLocaleString()} · {h.keyword}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!result && !loading && history.length === 0 && (
            <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center text-zinc-500">
              <FileText className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
              <p>Your generated posts will appear here.</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function BlogResult({ data, blogId, onCopy, copied, onRegenImage, imgRegen, onBroadcast, broadcasting, broadcastResult, onPublish, publishing, publishResult, autoPublishResults }) {
  return (
    <div className="space-y-4 fade-in" data-testid="blog-result">
      {data.featured_image && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden relative group" data-testid="blog-featured-image">
          <img src={data.featured_image} alt={data.title} className="w-full max-h-[420px] object-cover" />
          <Button onClick={onRegenImage} disabled={imgRegen} size="sm" variant="outline"
                  className="absolute top-3 right-3 bg-black/70 hover:bg-black/90 border-zinc-700 backdrop-blur rounded-sm"
                  data-testid="blog-regen-image">
            {imgRegen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Wand2 className="w-3.5 h-3.5 mr-1.5" /> Regenerate</>}
          </Button>
        </div>
      )}
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <span className="label-eyebrow">Title</span>
            <h2 className="font-display text-2xl font-medium tracking-tight mt-1" data-testid="blog-result-title">{data.title}</h2>
            <div className="flex gap-2 flex-wrap mt-3">
              {(data.tags || []).map((t) => (
                <Badge key={t} variant="outline" className="rounded-sm border-zinc-700 text-zinc-400 text-xs">#{t}</Badge>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <Button onClick={onCopy} variant="outline" size="sm" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="blog-copy-btn">
              {copied ? <><Check className="w-3.5 h-3.5 mr-1.5 text-[#10B981]" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy MD</>}
            </Button>
            <Button onClick={onPublish} disabled={publishing} size="sm"
                    className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="blog-publish-btn">
              {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Publish</>}
            </Button>
            <Button asChild variant="outline" size="sm" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="blog-publish-settings">
              <Link to="/integrations"><Settings className="w-3.5 h-3.5 mr-1.5" /> Publish settings</Link>
            </Button>
            <Button onClick={onBroadcast} disabled={broadcasting} variant="outline" size="sm"
                    className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="blog-broadcast-btn">
              {broadcasting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Share2 className="w-3.5 h-3.5 mr-1.5" /> Broadcast</>}
            </Button>
          </div>
        </div>
        {autoPublishResults && autoPublishResults.length > 0 && (
          <div className="text-xs px-3 py-2 rounded-sm border border-[#0055FF]/40 bg-[#0055FF]/10 text-[#3377FF] mb-2" data-testid="blog-auto-publish-result">
            ⚡ Auto-published to {autoPublishResults.filter(r => r.ok).length}/{autoPublishResults.length} integration(s).
          </div>
        )}
        {publishResult && (
          <div className={`text-xs px-3 py-2 rounded-sm border ${publishResult.ok ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-red-900/40 bg-red-950/30 text-red-400"}`}
               data-testid="blog-publish-result">
            {publishResult.msg}
            {publishResult.results && publishResult.results.map((r, i) => (
              <div key={i} className="mt-1">
                · {r.integration_name} ({r.integration_kind}): {r.ok ? <span className="text-[#10B981]">OK</span> : <span className="text-red-400">{r.error}</span>}
                {r.edit_link && <a href={r.edit_link} target="_blank" rel="noreferrer" className="ml-2 text-[#3377FF] hover:underline">edit →</a>}
              </div>
            ))}
          </div>
        )}
        {broadcastResult && (
          <div className={`text-xs px-3 py-2 rounded-sm border mt-2 ${broadcastResult.ok ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-red-900/40 bg-red-950/30 text-red-400"}`}
               data-testid="blog-broadcast-result">
            {broadcastResult.msg}
          </div>
        )}
      </div>

      <Tabs defaultValue="content">
        <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="blog-tabs">
          <TabsTrigger value="content" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-content">Content</TabsTrigger>
          <TabsTrigger value="seo" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-seo">SEO Meta</TabsTrigger>
          <TabsTrigger value="faq" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-faq">FAQ</TabsTrigger>
          <TabsTrigger value="links" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-links">Internal Links</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8 prose-invert max-w-none"
               dangerouslySetInnerHTML={{ __html: mdToHtml(data.content_markdown || "") }} data-testid="blog-content" />
        </TabsContent>

        <TabsContent value="seo" className="mt-4 space-y-4">
          <Box label="Meta title" testid="seo-meta-title">{data.meta_title}</Box>
          <Box label="Meta description" testid="seo-meta-desc">{data.meta_description}</Box>
          <Box label="Slug" testid="seo-slug"><code className="text-[#3377FF]">/{data.slug}</code></Box>
          <Box label="Featured image prompt" testid="seo-image-prompt"><span className="text-zinc-400 text-sm leading-relaxed">{data.featured_image_prompt}</span></Box>
        </TabsContent>

        <TabsContent value="faq" className="mt-4 space-y-3">
          {(data.faq || []).map((q, i) => (
            <div key={i} className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid={`faq-${i}`}>
              <div className="font-medium text-zinc-200">Q. {q.question}</div>
              <div className="mt-2 text-zinc-400 text-sm leading-relaxed">{q.answer}</div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="links" className="mt-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm divide-y divide-zinc-900">
            {(data.internal_links || []).map((l, i) => (
              <div key={i} className="px-6 py-4 flex items-center justify-between" data-testid={`link-${i}`}>
                <span className="text-zinc-200">{l.anchor}</span>
                <code className="text-[#3377FF] text-sm">{l.suggested_url_path}</code>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Box({ label, children, testid }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid={testid}>
      <span className="label-eyebrow">{label}</span>
      <div className="mt-2 text-zinc-200">{children}</div>
    </div>
  );
}
